import axiosInstance from "../CustomAxios/axiosInstance"
import salesService, { type Sale } from "./cash-register/salesService"

export interface StockStatistics {
    total_product_worth: number
    potential_profit: number
    total_stock_quantity: number
    average_stock_unit_price: number
}

export interface DashboardStats {
    categories: { name: string; count: number }[]
    subCategories: { name: string; count: number }[]
    brands: { name: string; count: number }[]
}

export interface RevenueData {
    date: string
    revenue: number
}

export interface RevenueComparisonData {
    current: RevenueData[]
    previous: RevenueData[]
}

export interface PeakHourData {
    order_date: string
    order_hour: number
    order_count: number
}

export interface CategoryProductsAndOrders {
    category: {
        id: number
        name: string
        image: string | null
    }
    products_count: number
    orders_count: number
    products: any[]
    orders: any[]
}

export interface SubcategoryProductsAndOrders {
    subcategory: {
        id: number
        name: string
        category: {
            id: number | null
            name: string
        }
    }
    products_count: number
    orders_count: number
    products: any[]
    orders: any[]
}

export interface CategoryData {
    name: string
    products: number
    orders: number
}

export interface SubCategoryData {
    name: string
    parentCategories: string
    products: number
    orders: number
}

// Cache management
interface CacheEntry<T> {
    data: T;
    timestamp: number;
}

class DashboardCache {
    private static instance: DashboardCache;
    private cache: Map<string, CacheEntry<any>>;
    private readonly CACHE_DURATION = 30 * 60 * 1000; // 30 minutes cache duration

    private constructor() {
        this.cache = new Map();
    }

    static getInstance(): DashboardCache {
        if (!DashboardCache.instance) {
            DashboardCache.instance = new DashboardCache();
        }
        return DashboardCache.instance;
    }

    get<T>(key: string): T | null {
        const entry = this.cache.get(key);
        if (entry && Date.now() - entry.timestamp < this.CACHE_DURATION) {
            return entry.data as T;
        }
        return null;
    }

    set<T>(key: string, data: T): void {
        this.cache.set(key, {
            data,
            timestamp: Date.now()
        });
    }

    clear(): void {
        this.cache.clear();
    }

    clearExpired(): void {
        const now = Date.now();
        // Convert Map to Array for iteration
        Array.from(this.cache.entries()).forEach(([key, entry]) => {
            if (now - entry.timestamp >= this.CACHE_DURATION) {
                this.cache.delete(key);
            }
        });
    }
}

const dashboardService = {
    cache: DashboardCache.getInstance(),

    async getStockStatistics(): Promise<StockStatistics> {
        const cacheKey = 'stockStatistics';
        const cachedData = this.cache.get<StockStatistics>(cacheKey);
        if (cachedData) return cachedData;

        try {
            const response = await axiosInstance.get<{ status: string; data: StockStatistics }>("/stock/statistics");
            if (response.data.status === "success" && response.data.data) {
                this.cache.set(cacheKey, response.data.data);
                return response.data.data;
            }
            throw new Error("Failed to fetch stock statistics");
        } catch (error) {
                        return {
                total_product_worth: 0,
                potential_profit: 0,
                total_stock_quantity: 0,
                average_stock_unit_price: 0,
            };
        }
    },

    async getAllCategoriesProductsAndOrders(): Promise<CategoryData[]> {
        const cacheKey = 'categories';
        const cachedData = this.cache.get<CategoryData[]>(cacheKey);
        if (cachedData) return cachedData;

        try {
            const response = await axiosInstance.get<{ status: string; data: any[] }>("/entities/categories/summary");
            
            if (response.data.status !== "success" || !response.data.data) {
                throw new Error("Failed to fetch categories summary");
            }

            const data = response.data.data.map(category => ({
                name: category.name,
                products: category.products_count,
                orders: category.orders_count
            }));
            this.cache.set(cacheKey, data);
            return data;
        } catch (error) {
                        return [];
        }
    },

    async getAllSubcategoriesProductsAndOrders(): Promise<SubCategoryData[]> {
        const cacheKey = 'subcategories';
        const cachedData = this.cache.get<SubCategoryData[]>(cacheKey);
        if (cachedData) return cachedData;

        try {
            const response = await axiosInstance.get<{ status: string; data: any[] }>("/entities/subcategories/getSubcategoriesProductsAndOrders");
            
            if (response.data.status !== "success" || !response.data.data) {
                throw new Error("Failed to fetch subcategories summary");
            }

            const data = response.data.data.map(subcategory => ({
                name: subcategory.subcategory.name,
                parentCategories: "N/A",
                products: subcategory.products_count,
                orders: subcategory.orders_count
            }));
            this.cache.set(cacheKey, data);
            return data;
        } catch (error) {
                        return [];
        }
    },

    async getRevenueComparisonData(options: {
        startDate: Date;
        endDate: Date;
        comparisonStartDate: Date;
        comparisonEndDate: Date;
        isYearly?: boolean;
    }): Promise<RevenueComparisonData> {
        try {
            const { startDate, endDate, comparisonStartDate, comparisonEndDate } = options;

            const response = await axiosInstance.get<RevenueComparisonData>(
                `/compare-earnings?period1_start=${startDate.toISOString().split('T')[0]}&period1_end=${endDate.toISOString().split('T')[0]}&period2_start=${comparisonStartDate.toISOString().split('T')[0]}&period2_end=${comparisonEndDate.toISOString().split('T')[0]}`);

            if (!response.data) {
                throw new Error("Failed to fetch revenue comparison data");
            }

            return response.data;
        } catch (error) {
                        throw error;
        }
    },

    async getPeakHourData(): Promise<PeakHourData[]> {
        const cacheKey = 'peakHours';
        const cachedData = this.cache.get<PeakHourData[]>(cacheKey);
        if (cachedData) return cachedData;

        try {
            const response = await axiosInstance.get<PeakHourData[]>("/orders/peak-hour");
            if (response.data) {
                this.cache.set(cacheKey, response.data);
            }
            return response.data || [];
        } catch (error) {
                        return [];
        }
    },

    async getDashboardData(): Promise<{
        stockStats: StockStatistics;
        categories: CategoryData[];
        subcategories: SubCategoryData[];
        peakHours: PeakHourData[];
    }> {
        try {
            const response = await axiosInstance.get<{
                status: string;
                data: {
                    stockStats: StockStatistics;
                    categories: CategoryData[];
                    subcategories: SubCategoryData[];
                    peakHours: PeakHourData[];
                }
            }>("/dashboard/data");

            if (response.data.status === "success" && response.data.data) {
                return response.data.data;
            }
            throw new Error("Failed to fetch dashboard data");
        } catch (error) {
                        return {
                stockStats: {
                    total_product_worth: 0,
                    potential_profit: 0,
                    total_stock_quantity: 0,
                    average_stock_unit_price: 0,
                },
                categories: [],
                subcategories: [],
                peakHours: []
            };
        }
    },

    async getMonthlyRevenueData(): Promise<RevenueComparisonData> {
        try {
            // Get all sales
            const sales = await salesService.getSales()
            
            if (!sales || sales.length === 0) {
                console.warn("No sales data returned from salesService.getSales()")
                return {
                    current: [],
                    previous: [],
                }
            }

            // Log a sample sale to check its structure
            if (sales.length > 0) {
                            }

            // Get current date info
            const now = new Date()
            const currentYear = now.getFullYear()
            const currentMonth = now.getMonth()

            // Create date objects for current and previous month ranges
            const currentMonthStart = new Date(currentYear, currentMonth, 1)
            const previousMonthStart = new Date(currentYear, currentMonth - 1, 1)

            // Get days in current and previous month
            const daysInCurrentMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
            const daysInPreviousMonth = new Date(currentYear, currentMonth, 0).getDate()

            // Initialize revenue data arrays with zero values for all days
            const currentMonthData: RevenueData[] = []
            const previousMonthData: RevenueData[] = []

            // Fill current month data
            for (let day = 1; day <= daysInCurrentMonth; day++) {
                const date = new Date(currentYear, currentMonth, day)
                const formattedDate = date.toISOString().split("T")[0]
                currentMonthData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Fill previous month data
            for (let day = 1; day <= daysInPreviousMonth; day++) {
                const date = new Date(currentYear, currentMonth - 1, day)
                const formattedDate = date.toISOString().split("T")[0]
                previousMonthData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Process sales data to populate revenue
            let currentMonthRevenue = 0
            let previousMonthRevenue = 0

            sales.forEach((sale: Sale) => {
                // Ensure sale.date is a string before creating a Date object
                if (!sale.date) {
                    console.warn("Sale missing date property:", sale)
                    return
                }

                try {
                    const saleDate = new Date(sale.date)
                    const saleYear = saleDate.getFullYear()
                    const saleMonth = saleDate.getMonth()
                    const saleDay = saleDate.getDate()

                    // Check if sale belongs to current month
                    if (saleYear === currentYear && saleMonth === currentMonth) {
                        const index = saleDay - 1
                        if (index >= 0 && index < currentMonthData.length) {
                            currentMonthData[index].revenue += sale.totalAmount
                            currentMonthRevenue += sale.totalAmount
                        }
                    }
                    // Check if sale belongs to previous month
                    else if (
                        (saleYear === currentYear && saleMonth === currentMonth - 1) ||
                        (currentMonth === 0 && saleYear === currentYear - 1 && saleMonth === 11)
                    ) {
                        const index = saleDay - 1
                        if (index >= 0 && index < previousMonthData.length) {
                            previousMonthData[index].revenue += sale.totalAmount
                            previousMonthRevenue += sale.totalAmount
                        }
                    }
                } catch (error) {
                                    }
            })

                                                
            return {
                current: currentMonthData,
                previous: previousMonthData,
            }
        } catch (error) {
                        return {
                current: [],
                previous: [],
            }
        }
    },

    async getMonthlyRevenueDataForDate(date: Date): Promise<RevenueComparisonData> {
        try {
            // Get all sales
            const sales = await salesService.getSales()
                        
            if (!sales || sales.length === 0) {
                console.warn("No sales data returned from salesService.getSales()")
                return {
                    current: [],
                    previous: [],
                }
            }

            // Get date info from the provided date
            const targetYear = date.getFullYear()
            const targetMonth = date.getMonth()

            // Create date objects for target and previous month ranges
            const targetMonthStart = new Date(targetYear, targetMonth, 1)
            const previousMonthStart = new Date(targetYear, targetMonth - 1, 1)

            // Get days in target and previous month
            const daysInTargetMonth = new Date(targetYear, targetMonth + 1, 0).getDate()
            const daysInPreviousMonth = new Date(targetYear, targetMonth, 0).getDate()

            // Initialize revenue data arrays with zero values for all days
            const targetMonthData: RevenueData[] = []
            const previousMonthData: RevenueData[] = []

            // Fill target month data
            for (let day = 1; day <= daysInTargetMonth; day++) {
                const date = new Date(targetYear, targetMonth, day)
                const formattedDate = date.toISOString().split("T")[0]
                targetMonthData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Fill previous month data
            for (let day = 1; day <= daysInPreviousMonth; day++) {
                const date = new Date(targetYear, targetMonth - 1, day)
                const formattedDate = date.toISOString().split("T")[0]
                previousMonthData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Process sales data to populate revenue
            let targetMonthRevenue = 0
            let previousMonthRevenue = 0

            sales.forEach((sale: Sale) => {
                if (!sale.date) {
                    console.warn("Sale missing date property:", sale)
                    return
                }

                try {
                    const saleDate = new Date(sale.date)
                    const saleYear = saleDate.getFullYear()
                    const saleMonth = saleDate.getMonth()
                    const saleDay = saleDate.getDate()

                    // Check if sale belongs to target month
                    if (saleYear === targetYear && saleMonth === targetMonth) {
                        const index = saleDay - 1
                        if (index >= 0 && index < targetMonthData.length) {
                            targetMonthData[index].revenue += sale.totalAmount
                            targetMonthRevenue += sale.totalAmount
                        }
                    }
                    // Check if sale belongs to previous month
                    else if (
                        (saleYear === targetYear && saleMonth === targetMonth - 1) ||
                        (targetMonth === 0 && saleYear === targetYear - 1 && saleMonth === 11)
                    ) {
                        const index = saleDay - 1
                        if (index >= 0 && index < previousMonthData.length) {
                            previousMonthData[index].revenue += sale.totalAmount
                            previousMonthRevenue += sale.totalAmount
                        }
                    }
                } catch (error) {
                                    }
            })

                        
            return {
                current: targetMonthData,
                previous: previousMonthData,
            }
        } catch (error) {
                        return {
                current: [],
                previous: [],
            }
        }
    },

    async getYearlyRevenueData(): Promise<RevenueComparisonData> {
        try {
            // Get all sales
            const sales = await salesService.getSales()
            
            if (!sales || sales.length === 0) {
                console.warn("No sales data returned from salesService.getSales() for yearly data")
                return {
                    current: [],
                    previous: [],
                }
            }

            // Get current date info
            const now = new Date()
            const currentYear = now.getFullYear()
            const previousYear = currentYear - 1

            // Initialize revenue data arrays with zero values for all months
            const currentYearData: RevenueData[] = []
            const previousYearData: RevenueData[] = []

            // Fill current year data (12 months)
            for (let month = 0; month < 12; month++) {
                const date = new Date(currentYear, month, 1)
                const formattedDate = date.toISOString().split("T")[0].substring(0, 7) // YYYY-MM format
                currentYearData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Fill previous year data (12 months)
            for (let month = 0; month < 12; month++) {
                const date = new Date(previousYear, month, 1)
                const formattedDate = date.toISOString().split("T")[0].substring(0, 7) // YYYY-MM format
                previousYearData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Process sales data to populate revenue
            let currentYearRevenue = 0
            let previousYearRevenue = 0

            sales.forEach((sale: Sale) => {
                if (!sale.date) {
                    console.warn("Sale missing date property:", sale)
                    return
                }

                try {
                    const saleDate = new Date(sale.date)
                    const saleYear = saleDate.getFullYear()
                    const saleMonth = saleDate.getMonth()

                    // Check if sale belongs to current year
                    if (saleYear === currentYear) {
                        currentYearData[saleMonth].revenue += sale.totalAmount
                        currentYearRevenue += sale.totalAmount
                    }
                    // Check if sale belongs to previous year
                    else if (saleYear === previousYear) {
                        previousYearData[saleMonth].revenue += sale.totalAmount
                        previousYearRevenue += sale.totalAmount
                    }
                } catch (error) {
                                    }
            })

                                                
            return {
                current: currentYearData,
                previous: previousYearData,
            }
        } catch (error) {
                        return {
                current: [],
                previous: [],
            }
        }
    },

    async getYearlyRevenueDataForYear(year: number): Promise<RevenueComparisonData> {
        try {
            // Get all sales
            const sales = await salesService.getSales()
                        
            if (!sales || sales.length === 0) {
                console.warn("No sales data returned from salesService.getSales() for yearly data")
                return {
                    current: [],
                    previous: [],
                }
            }

            const previousYear = year - 1

            // Initialize revenue data arrays with zero values for all months
            const currentYearData: RevenueData[] = []
            const previousYearData: RevenueData[] = []

            // Fill current year data (12 months)
            for (let month = 0; month < 12; month++) {
                const date = new Date(year, month, 1)
                const formattedDate = date.toISOString().split("T")[0].substring(0, 7) // YYYY-MM format
                currentYearData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Fill previous year data (12 months)
            for (let month = 0; month < 12; month++) {
                const date = new Date(previousYear, month, 1)
                const formattedDate = date.toISOString().split("T")[0].substring(0, 7) // YYYY-MM format
                previousYearData.push({
                    date: formattedDate,
                    revenue: 0,
                })
            }

            // Process sales data to populate revenue
            let currentYearRevenue = 0
            let previousYearRevenue = 0

            sales.forEach((sale: Sale) => {
                if (!sale.date) {
                    console.warn("Sale missing date property:", sale)
                    return
                }

                try {
                    const saleDate = new Date(sale.date)
                    const saleYear = saleDate.getFullYear()
                    const saleMonth = saleDate.getMonth()

                    // Check if sale belongs to target year
                    if (saleYear === year) {
                        currentYearData[saleMonth].revenue += sale.totalAmount
                        currentYearRevenue += sale.totalAmount
                    }
                    // Check if sale belongs to previous year
                    else if (saleYear === previousYear) {
                        previousYearData[saleMonth].revenue += sale.totalAmount
                        previousYearRevenue += sale.totalAmount
                    }
                } catch (error) {
                                    }
            })

                        
            return {
                current: currentYearData,
                previous: previousYearData,
            }
        } catch (error) {
                        return {
                current: [],
                previous: [],
            }
        }
    },

    clearCache(): void {
        this.cache.clear();
    },

    clearExpiredCache(): void {
        this.cache.clearExpired();
    }
}

export default dashboardService